import React, {useState, useMemo, useCallback} from 'react';
import {View, StyleSheet, Image as RNImage, useWindowDimensions, Platform} from 'react-native';
import RenderHtml, {
  CustomRendererProps,
  TBlock,
  TText,
  RenderersProps,
  TChildrenRenderer,
} from 'react-native-render-html';
import {Image} from '../image';
import {Props} from './model';
import {useStyles, useStylesHtml} from '../../hooks/styles';
import {DeviceSize} from '../../theme/helpers';
import {Text} from '../text';
import {images} from '../../../assets/icons';
import {openExternalLink} from '../../pipes';
import {useIsDesktop} from '../../hooks/useIsDesktop';
import {useSelector} from 'react-redux';
import {themeSelector} from '../../../redux/selectors/runtime/theme';
import {VisImpStatus} from '../../models/visuallyImpairedSettings';
import {ErrorBoundary} from '../errorBoundary';
import {getFontWeight} from '../../theme/default';

const desktopMainContentWidth = 874;
const iframeHeightToWidthRatio = 9 / 16;

const dataSetAttrs = {
  dataSet: {
    class: 'html',
  },
};

// Custom renderer for anchor tags
function AnchorRenderer({tnode, TDefaultRenderer, ...props}: CustomRendererProps<TText | TBlock>) {
  const href = tnode.attributes.href;
  const target = tnode.attributes.target;

  const onPress = async () => {
    if (href) {
      await openExternalLink(href, target);
    }
  };

  return <TDefaultRenderer {...props} tnode={tnode} onPress={onPress} />;
}

export default ({content, style, baseFontStyle, testID}: Props) => {
  const {width: windowWidth} = useWindowDimensions();
  const themeSettings = useSelector(themeSelector());
  const isVisuallyImpairedSettingActive = themeSettings.visuallyImpaired === VisImpStatus.Active;
  const [mainImageHeight, setMainImageHeight] = React.useState(0);
  const isDesktop = useIsDesktop();

  const styles = useStyles(({theme, addShadow}) => ({
    imgAlt: {
      borderRadius: 5,
      height: 100,
      paddingHorizontal: 10,
      backgroundColor: theme.color.loaderBgColor,
      borderStyle: 'dotted',
      borderWidth: 2,
      borderColor: theme.color.borderColor,
      justifyContent: 'center',
      alignItems: 'center',
      ...addShadow({
        color: theme.color.primaryBlackColor,
        opacity: 0.1,
        height: 2,
        radius: 17,
      }),
    },
    imgAltText: {
      color: theme.color.textColor,
    },
    ulPrefix: {
      width: 16,
      height: 24,
      color: theme.color.primaryColor,
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 8,
    },
    olPrefix: {
      ...theme.fonts.body,
      color: theme.color.textColor,
      marginRight: 8,
      minWidth: 20,
      textAlign: 'right',
    },
  }));

  const tagsStyles = useStylesHtml(({theme, mediaOnly, deviceWidth}) => ({
    body: {
      ...theme.fonts.h5Light,
      color: theme.color.textColor,
    },
    a: {
      color: theme.color.primaryColor,
      textDecorationLine: 'none',
    },
    s: {
      textDecorationLine: 'line-through',
    },
    del: {
      textDecorationLine: 'line-through',
    },
    strike: {
      textDecorationLine: 'line-through',
    },
    b: {
      marginTop: 30,
      ...(Platform.OS === 'android'
        ? {
            fontFamily: theme.fonts.fontFamily.regular,
            fontWeight: '700',
          }
        : {
            fontFamily: theme.fonts.fontFamily.bold,
            fontWeight: getFontWeight('bold'),
          }),
      color: theme.color.textColor,
    },
    p: {
      ...theme.fonts.body,
      color: theme.color.textColor,
      marginBottom: 16,
    },
    strong: {
      ...(Platform.OS === 'android'
        ? {
            fontFamily: theme.fonts.fontFamily.regular,
            fontWeight: '700',
          }
        : {
            fontFamily: theme.fonts.fontFamily.bold,
            fontWeight: getFontWeight('bold'),
          }),
      color: theme.color.textColor,
    },
    em: {
      fontStyle: 'italic',
      color: theme.color.textColor,
    },
    i: {
      fontStyle: 'italic',
      color: theme.color.textColor,
      marginVertical: 20,
    },
    li: {
      marginBottom: 10,
      color: theme.color.textColor,
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
    ul: {
      marginBottom: 10,
    },
    ol: {
      marginBottom: 10,
      paddingLeft: 10,
    },
    blockquote: {
      paddingHorizontal: 10,
      paddingVertical: 10,
      color: theme.color.textColor,
    },
    time: {
      color: theme.color.labelText,
      ...mediaOnly(DeviceSize.desktop, theme.fonts.bodySmallLight, theme.fonts.label),
      lineHeight: 20,
    },
    h1: {
      ...theme.fonts.h1,
      color: theme.color.textColor,
      marginBottom: 16,
    },
    h2: {
      ...theme.fonts.h2,
      color: theme.color.textColor,
      marginBottom: 16,
    },
    h3: {
      ...theme.fonts.h3,
      color: theme.color.textColor,
      marginBottom: 16,
    },
    img: {
      width: '100%',
      ...mediaOnly(
        DeviceSize.desktop,
        {
          marginBottom: 24,
        },
        {
          marginBottom: 15,
        },
      ),
    },
    figure: {},
    figcaption: {},
    iframe: {
      maxWidth: '100%',
      marginBottom: 15,
      ...mediaOnly(
        DeviceSize.desktop,
        {height: desktopMainContentWidth * iframeHeightToWidthRatio},
        {height: deviceWidth * iframeHeightToWidthRatio},
      ),
    },
  }));

  const classesStyles = useStylesHtml(({theme}) => ({
    'text-big': {
      fontSize: 24,
      lineHeight: 32,
    },
    'text-huge': {
      fontSize: 24,
      lineHeight: 32,
    },
    'text-tiny': {
      fontSize: 12,
      lineHeight: 16,
    },
    'text-small': {
      fontSize: 12,
      lineHeight: 16,
    },
    'text-medium-size': {
      fontSize: 20,
      lineHeight: 28,
    },
    'text-gray': {
      color: theme.color.labelText,
    },
    'text-main': {
      color: theme.color.primaryColor,
    },
    'text-red': {
      color: theme.html.textRed,
    },
    'text-normal': {
      ...theme.fonts.body,
      fontSize: 16,
      lineHeight: 22,
      fontWeight: getFontWeight('400'),
      fontFamily: theme.fonts.fontFamily.regular,
      letterSpacing: theme.fonts.body.letterSpacing ?? 0,
    },
    'text-medium': {
      ...theme.fonts.h6,
      fontSize: 16,
      lineHeight: 19,
      fontWeight: getFontWeight('500'),
      fontFamily: theme.fonts.fontFamily.medium,
      letterSpacing: theme.fonts.body.letterSpacing ?? 0,
    },
    'text-center': {
      textAlign: 'center',
    },
    'text-underline': {
      textDecorationLine: 'underline',
    },
  }));

  const [imagesHeights, setImagesHeights] = useState<Record<string, number>>({});

  // Custom renderer for images - memoized to prevent re-renders
  const ImageRenderer = useCallback(
    ({tnode}: CustomRendererProps<TBlock>) => {
      const src = tnode.attributes.src;
      const alt = tnode.attributes.alt;

      const calculatedHeight = imagesHeights[src];

      if (src && !calculatedHeight) {
        const imageWidth = (mainImageHeight / 2) * 3;
        RNImage.getSize(src, (resultWidth, resultHeight) => {
          setImagesHeights((prevState) => ({...prevState, [src]: (imageWidth / resultWidth) * resultHeight}));
        });
      }

      return src ? (
        <View style={tagsStyles.img as any}>
          <Image
            source={{uri: src}}
            width={isDesktop ? desktopMainContentWidth : undefined}
            height={calculatedHeight ?? mainImageHeight}
            style={{height: calculatedHeight ?? mainImageHeight}}
            resizeMode='contain'
          />
        </View>
      ) : (
        <View style={[styles.imgAlt, tagsStyles.img as any]}>
          <Text style={styles.imgAltText}>{alt}</Text>
        </View>
      );
    },
    [imagesHeights, mainImageHeight, isDesktop, tagsStyles.img, styles.imgAlt, styles.imgAltText],
  );

  // Custom renderer for unordered lists - memoized to prevent re-renders
  const UlRenderer = useCallback(
    ({tnode}: CustomRendererProps<TBlock>) => {
      return (
        <View style={tagsStyles.ul as any}>
          {tnode.children.map((child, index) => (
            <View key={index} style={tagsStyles.li as any}>
              <View style={styles.ulPrefix}>
                <images.Check width={16} height={16} color={styles.ulPrefix.color as string} />
              </View>
              <View style={{flex: 1}}>
                <TChildrenRenderer tchildren={[child]} />
              </View>
            </View>
          ))}
        </View>
      );
    },
    [tagsStyles.ul, tagsStyles.li, styles.ulPrefix],
  );

  // Custom renderer for ordered lists - memoized to prevent re-renders
  const OlRenderer = useCallback(
    ({tnode}: CustomRendererProps<TBlock>) => {
      return (
        <View style={tagsStyles.ol as any}>
          {tnode.children.map((child, index) => (
            <View key={index} style={tagsStyles.li as any}>
              <Text style={styles.olPrefix}>{index + 1}. </Text>
              <View style={{flex: 1}}>
                <TChildrenRenderer tchildren={[child]} />
              </View>
            </View>
          ))}
        </View>
      );
    },
    [tagsStyles.ol, tagsStyles.li, styles.olPrefix],
  );

  // Custom renderer for figure
  const FigureRenderer = useCallback(
    ({tnode, TDefaultRenderer, ...props}: CustomRendererProps<TBlock>) => {
      return (
        <View style={tagsStyles.figure as any}>
          <TDefaultRenderer {...props} tnode={tnode} />
        </View>
      );
    },
    [tagsStyles.figure],
  );

  // Custom renderer for figcaption
  const FigcaptionRenderer = useCallback(({tnode, TDefaultRenderer, ...props}: CustomRendererProps<TBlock>) => {
    return <TDefaultRenderer {...props} tnode={tnode} />;
  }, []);

  const renderers: RenderersProps['renderers'] = useMemo(
    () => ({
      a: AnchorRenderer,
      ul: UlRenderer,
      ol: OlRenderer,
      img: ImageRenderer,
      figure: FigureRenderer,
      figcaption: FigcaptionRenderer,
    }),
    [UlRenderer, OlRenderer, ImageRenderer],
  );

  const setMainImageHeightByWidth = (width: number) => {
    setMainImageHeight((width / 3) * 2);
  };

  if (!content) {
    return null;
  }

  const baseStyle = {
    ...StyleSheet.flatten(tagsStyles.body as any),
    ...StyleSheet.flatten(baseFontStyle ?? {}),
  };

  const contentWidth = isDesktop ? desktopMainContentWidth : windowWidth;

  return (
    <View onLayout={(event) => setMainImageHeightByWidth(event.nativeEvent.layout.width)} style={style} testID={testID}>
      <ErrorBoundary>
        <RenderHtml
          contentWidth={contentWidth}
          source={{html: content}}
          tagsStyles={tagsStyles as any}
          classesStyles={classesStyles as any}
          baseStyle={baseStyle}
          enableExperimentalMarginCollapsing
          ignoredStyles={isVisuallyImpairedSettingActive ? ['color', 'backgroundColor'] : undefined}
          renderers={renderers}
          {...dataSetAttrs}
        />
      </ErrorBoundary>
    </View>
  );
};
